/**
 * MEV-Med Theme Configuration
 * Custom Material-UI theme with MEV-Med brand colors
 */

import { createTheme } from '@mui/material/styles';
import branding from '../config/branding';

// ============================================================================
// Color Tokens (driven by branding config, with hardcoded fallbacks)
// Use estas constantes em qualquer lugar do código:
//   import { MAGENTA, TEAL, VIOLET, SLATE } from '../theme';
// ============================================================================

const c = branding.colors;

// Marca (Primary)
export const MAGENTA       = c.primaryMain;
export const MAGENTA_LIGHT = c.primaryLight;
export const MAGENTA_DARK  = c.primaryDark;

// Complementar (Secondary)
export const TEAL       = c.secondaryMain;
export const TEAL_LIGHT = c.secondaryLight;
export const TEAL_DARK  = c.secondaryDark;

// Análogo (Accent)
export const VIOLET       = c.accentMain;
export const VIOLET_LIGHT = c.accentLight;
export const VIOLET_DARK  = c.accentDark;

// Neutro (header, backgrounds escuros)
export const SLATE       = c.headerBg;
export const SLATE_LIGHT = '#334155';
export const SLATE_DARK  = '#0f172a';

// Semânticas (não personalizáveis — significado fixo)
export const GREEN       = '#16a34a';
export const GREEN_LIGHT = '#4ade80';
export const GREEN_DARK  = '#15803d';

export const AMBER       = '#d97706';
export const AMBER_LIGHT = '#fbbf24';
export const AMBER_DARK  = '#b45309';

export const RED       = '#dc2626';
export const RED_LIGHT = '#f87171';
export const RED_DARK  = '#b91c1c';

// ============================================================================
// Palette objects (usados pelo MUI theme)
// ============================================================================

export const mevColors = {
  primary: {
    main: MAGENTA,
    light: MAGENTA_LIGHT,
    dark: MAGENTA_DARK,
    contrastText: '#ffffff',
  },
  secondary: {
    main: TEAL,
    light: TEAL_LIGHT,
    dark: TEAL_DARK,
    contrastText: '#ffffff',
  },
  brand: {
    main: VIOLET,
    light: VIOLET_LIGHT,
    dark: VIOLET_DARK,
    contrastText: '#ffffff',
  },
  success: {
    main: GREEN,
    light: GREEN_LIGHT,
    dark: GREEN_DARK,
    contrastText: '#ffffff',
  },
  warning: {
    main: AMBER,
    light: AMBER_LIGHT,
    dark: AMBER_DARK,
    contrastText: '#ffffff',
  },
  error: {
    main: RED,
    light: RED_LIGHT,
    dark: RED_DARK,
    contrastText: '#ffffff',
  },
  info: {
    main: VIOLET,
    light: VIOLET_LIGHT,
    dark: VIOLET_DARK,
    contrastText: '#ffffff',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================================================
// Theme — MUI CSS variables + colorSchemes (official dark mode approach)
// ============================================================================

const sharedPalette = {
  primary: mevColors.primary,
  secondary: mevColors.secondary,
  brand: mevColors.brand,
  success: mevColors.success,
  warning: mevColors.warning,
  error: mevColors.error,
  info: mevColors.info,
  grey: mevColors.grey,
};

export const mevTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data',
  },
  colorSchemes: {
    light: {
      palette: {
        ...sharedPalette,
        background: { default: '#f8f9fa', paper: '#ffffff' },
        text: { primary: '#212121', secondary: '#757575' },
        divider: 'rgba(0, 0, 0, 0.12)',
        AppBar: { defaultBg: SLATE },
      },
    },
    dark: {
      palette: {
        ...sharedPalette,
        background: { default: '#1e1e2d', paper: '#272738' },
        text: { primary: '#e0e0e0', secondary: '#a0a0b0' },
        divider: 'rgba(255, 255, 255, 0.12)',
        AppBar: { defaultBg: '#1a1a2e' },
      },
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        },
        containedPrimary: {
          backgroundColor: mevColors.primary.main,
          '&:hover': { backgroundColor: mevColors.primary.dark },
        },
        containedSecondary: {
          backgroundColor: mevColors.secondary.main,
          '&:hover': { backgroundColor: mevColors.secondary.dark },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mevColors.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mevColors.primary.main,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          ...theme.applyStyles('dark', {
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }),
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          borderRadius: 0,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.875rem',
          lineHeight: 1.6,
          padding: '10px 14px',
          maxWidth: 480,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 16 },
        // Estes dois slots valem para TODA variante, `outlined` inclusive — é por isso que um
        // chip pedido como contorno aparece sólido. O efeito colateral estava no mouse em cima:
        // o hover que o MUI escreve para o outlined clareia o fundo para ~4% da cor, e o texto
        // continuava branco por causa daqui — branco sobre claro, ilegível justamente enquanto
        // se lê o rótulo para clicar. O hover abaixo escurece em vez de clarear, e mantém o
        // texto. Só no chip clicável: chip de status não reage ao mouse e não deve mudar de cor.
        colorPrimary: {
          backgroundColor: mevColors.primary.main,
          color: mevColors.primary.contrastText,
          '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus-visible': {
            backgroundColor: mevColors.primary.dark,
            color: mevColors.primary.contrastText,
          },
        },
        colorSecondary: {
          backgroundColor: mevColors.secondary.main,
          color: mevColors.secondary.contrastText,
          '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus-visible': {
            backgroundColor: mevColors.secondary.dark,
            color: mevColors.secondary.contrastText,
          },
        },
      },
    },
  },
});

// Declare module augmentation for custom colors
declare module '@mui/material/styles' {
  interface Palette {
    brand: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }

  interface PaletteOptions {
    brand?: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
}

export default mevTheme;